import { Box, IconButton, Menu, MenuItem, Button } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import React, { SyntheticEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import SearchBar from '../../components/SearchBar';
import { showError, showToast } from '../../constants/toasts';
import ApprovedIndividualsList from '../../features/individuals/ApprovedIndividualsList';
import MainContainer from '../../layout/MainContainer';
import { ConfirmationModal } from '../../Modals/confirmationModal';
import {
  useLazyDeleteUserQuery,
  useLazyGetUsersQuery,
  useUpdateUserProfileMutation,
} from '../../service/auth';
import { CommonBody, Users } from '../../types/General';
import { generateEncryptedKeyBody } from '../../utils/crypto';
import { APPROVAL_STATUS, UPDATE_TYPES, USER_TYPE } from '../../utils/enums';
import { isValidInput } from '../../utils/validations';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const Individuals = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [valueTabs, setValueTabs] = useState(APPROVAL_STATUS.PENDING);
  const [newIndividuals, setNewIndividuals] = useState<number>(0);
  const [approvedIndividuals, setApprovedIndividuals] = useState<number>(0);
  const [rejectedIndividuals, setRejectedIndividuals] = useState<number>(0);
  const [selectedId, setSelectedId] = useState('');

  const [open, setOpen] = useState(false);
  const [modalType, setModalType] = useState('');

  const [getUsers] = useLazyGetUsersQuery();
  const [deleteUser] = useLazyDeleteUserQuery();
  const [updateUser] = useUpdateUserProfileMutation();

  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<Users[]>([]);
  const [page, setPage] = useState<number>(1);

  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeTabs = (event: SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };

  const [anchorEl1, setAnchorEl1] = React.useState<null | HTMLElement>(null);
  const open1 = Boolean(anchorEl1);
  const handleClick1 = (event: React.MouseEvent<HTMLButtonElement>, rowId: string) => {
    setAnchorEl1(event.currentTarget);
    setSelectedId(rowId);
  };
  const handleClose1 = () => {
    setAnchorEl1(null);
    setSelectedId('');
  };
  const columns: GridColDef<(typeof users)[number]>[] = [
    {
      flex: 0.8,
      minWidth: 200,
      field: 'name',
      headerName: 'User Details',
      editable: false,
      sortable: false,

      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <figure onClick={() => navigate(`/profile-detail/individual/${row?._id}`)}>
              <img src={row?.image || '/static/images/user_placeholder.png'} alt="" />
            </figure>
            <p>
              <b>{row?.name || '-'}</b>
              {row?.dialCode} {row?.phoneNo || '-'} <br />
              {row?.email || '-'}
            </p>
          </div>
        );
      },
    },

    {
      flex: 1,
      minWidth: 300,
      field: 'address',
      headerName: 'Address',
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <p>{row?.addressObject?.address || '-'}</p>
          </div>
        );
      },
    },

    {
      flex: 0.1,
      minWidth: 80,
      field: 'action',
      headerName: 'Action',
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="table_actions">
            <Box>
              <IconButton
                aria-controls={open1 ? 'basic-menu1' : undefined}
                aria-haspopup="true"
                aria-expanded={open1 ? 'true' : undefined}
                onClick={(event) => handleClick1(event, row._id)}
              >
                <img src="/static/images/more_icon.svg" alt="" />
              </IconButton>
              <Menu
                className="setting_menu"
                id="basic-menu1"
                anchorEl={anchorEl1}
                open={open1}
                onClose={handleClose1}
                MenuListProps={{
                  'aria-labelledby': 'basic-button1',
                }}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: 'visible',
                    boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.05)',
                    borderRadius: '8px',
                    mt: 1,
                  },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem
                  onClick={() => {
                    if (selectedId) {
                      setModalType('accept');
                      setAnchorEl1(null);
                      setOpen(true);
                    }
                  }}
                >
                  Approve
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    if (selectedId) {
                      setAnchorEl1(null);
                      setModalType('reject');
                      setOpen(true);
                    }
                  }}
                >
                  Reject
                </MenuItem>
                <MenuItem onClick={() => navigate(`/profile-detail/individual/${selectedId}`)}>
                  View
                </MenuItem>
                <MenuItem onClick={() => navigate(`/edit-profile-detail/individual/${selectedId}`)}>
                  Edit
                </MenuItem>
              </Menu>
            </Box>
          </div>
        );
      },
    },
  ];

  const handleDeleteUser = async () => {
    setLoading(true);
    try {
      const response = await deleteUser({
        id: selectedId,
      }).unwrap();
      setLoading(false);

      if (response?.statusCode === 200) {
        showToast('Deleted successfully');
        getUsersList();
      } else {
        setUsers([]);
      }
    } catch (error: any) {
      showError(error?.data?.message || '');
      setLoading(false);
    }
  };

  const handleUserUpdate = async (id: string, status: any, type: string) => {
    const body = {
      ...(type === UPDATE_TYPES.BLOCK ? { isBlocked: status } : {}),
    };

    try {
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await updateUser({
        body: encryptedBody,
        id: id,
      }).unwrap();
      if (response?.statusCode === 200) {
        const updatedData = users?.map((item) => {
          if (item?._id === id) {
            return {
              ...item,
              isBlocked: status,
            };
          }
          return item;
        });
        setUsers(updatedData);
        if (status === true) {
          showToast('Blocked successfully');
        } else {
          showToast('Unblocked successfully');
        }
      } else {
        showError('Please try again');
      }
    } catch (error: any) {
      console.error(error, 'errror');
      showError(error?.data?.message || '');
    }
  };

  const handleApproveReject = async () => {
    const body = {
      status: modalType === 'accept' ? 2 : 3,
    };

    try {
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await updateUser({
        body: encryptedBody,
        id: selectedId,
      }).unwrap();
      if (response?.statusCode === 200) {
        showToast(
          modalType === 'accept'
            ? 'Individual approved successfully'
            : 'Individual rejected successfully'
        );
        if (modalType === 'accept') {
          setValueTabs(APPROVAL_STATUS.APPROVED);
          setPage(1);
        } else {
          setValueTabs(APPROVAL_STATUS.REJECTED);
          setPage(1);
        }
      } else {
        showError('Please try again');
      }
    } catch (error: any) {
      console.error(error, 'errror');
      showError(error?.data?.message || '');
    }
  };

  const getUsersList = async () => {
    setLoading(true);
    try {
      const response = await getUsers({
        page: page,
        limit: 10,
        search: debouncedSearchTerm.trim(),
        role: USER_TYPE.INDIVIDUAL,
        // status: valueTabs + 1,
      }).unwrap();
      setLoading(false);

      if (response?.statusCode === 200) {
        setUsers(response?.data?.user || []);
        setCount(response?.data?.count);
        setNewIndividuals(response?.data?.pendingCount);
        setApprovedIndividuals(response?.data?.approvedCount);
        setRejectedIndividuals(response?.data?.rejectCount);
      } else {
        setUsers([]);
      }
    } catch (error: any) {
      showError(error?.data?.message || '');
      setLoading(false);
    }
  };

  useEffect(() => {
    getUsersList();
  }, [debouncedSearchTerm, page, valueTabs]);

  return (
    <MainContainer>
      <div className="main_title">
        <h1> Individuals</h1>
        <p>
          <span onClick={() => navigate('/dashboard')}>Dashboard</span> - Registrations -
          Individuals
        </p>
      </div>
      <div className="cards">
        <Box className="custom_tabs" sx={{ width: '100%' }}>
          <div className="table_header">
            <div className="left_s">
              <Box className="search_box">
                <SearchBar
                  searchTerm={searchTerm}
                  placeholder="Search individuals"
                  setDebouncedSearchTerm={setDebouncedSearchTerm}
                  value={searchTerm}
                  onCross={() => setSearchTerm('')}
                  onChange={(val: any) => {
                    if (isValidInput(val.target.value)) {
                      setSearchTerm(val.target.value);
                    }
                  }}
                />
              </Box>
            </div>
            <div className="right_s">
              <Box>
                <Button className="btn btn_highlight">
                  <img src="/static/images/upload_icon.svg" alt="Icon" /> Export CSV
                </Button>
              </Box>
            </div>
          </div>

          {/* <Tabs
            value={valueTabs}
            onChange={handleChangeTabs}
            aria-label="basic tabs example"
            className="custom_tabs_links"
          >
            <Tab
              label={`New Individuals (${newIndividuals})`}
              {...a11yProps(APPROVAL_STATUS.PENDING)}
            />
            <Tab
              label={`Approved Individuals (${approvedIndividuals})`}
              {...a11yProps(APPROVAL_STATUS.APPROVED)}
            />
            <Tab
              label={`Rejected Individuals (${rejectedIndividuals})`}
              {...a11yProps(APPROVAL_STATUS.REJECTED)}
            />
          </Tabs> */}

          {/* <CustomTabPanel value={valueTabs} index={APPROVAL_STATUS.PENDING}>
            <NewIndividualsList
              rows={users}
              columns={columns}
              count={count}
              page={page}
              onPageChange={handlePageChange}
              loading={loading}
            />
          </CustomTabPanel> */}
          {/* <CustomTabPanel value={valueTabs} index={APPROVAL_STATUS.APPROVED}> */}
          <ApprovedIndividualsList
            count={count}
            rows={users}
            page={page}
            onPageChange={handlePageChange}
            loading={loading}
            setUsers={setUsers}
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            handleDeleteUser={handleDeleteUser}
            handleUserUpdate={handleUserUpdate}
            setModalType={setModalType}
            setOpen={setOpen}
          />
          {/* </CustomTabPanel> */}
          {/* <CustomTabPanel value={valueTabs} index={APPROVAL_STATUS.REJECTED}>
            <RejectedIndividualsList
              count={count}
              rows={users}
              page={page}
              onPageChange={handlePageChange}
              loading={loading}
              setSelectedId={setSelectedId}
              setOpen={setOpen}
              setModalType={setModalType}
            />
          </CustomTabPanel> */}
        </Box>
      </div>
      <ConfirmationModal
        text={
          modalType === 'delete'
            ? 'Are you sure to delete this individual?'
            : modalType === 'accept'
              ? 'Are you sure to approve this individual?'
              : modalType === 'reject'
                ? 'Are you sure to reject this individual?'
                : ''
        }
        openModal={open}
        setOpenModal={setOpen}
        handleSubmit={
          modalType === 'delete'
            ? handleDeleteUser
            : modalType === 'accept' || modalType === 'reject'
              ? () => handleApproveReject()
              : () => {}
        }
      />
    </MainContainer>
  );
};

export default Individuals;
